import { OcmPoi, OcmUsageType } from '../../../../stores/types/ocm.interface';
import { FinderResult } from '../types';

export interface ContactDetails {
  phones: string[];
  email: string | null;
  websiteUrl: string | null;
  bookingUrl: string | null;
}

export const formatPoiAddress = (poi: OcmPoi) => {
  const address = poi.AddressInfo;
  return [
    address?.AddressLine1,
    address?.AddressLine2,
    address?.Town,
    address?.StateOrProvince,
    address?.Postcode,
  ]
    .filter(Boolean)
    .join(', ');
};

/** OCM URLs are crowd-sourced: only allow http(s), adding https:// when the scheme is missing */
export const getSafeUrl = (url?: string | null) => {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return /^https?:$/.test(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

export const getUrlHost = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const getPhoneHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

const toValidEmail = (email?: string | null) =>
  email && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) ? email.trim() : null;

const toPhones = (...phones: (string | null | undefined)[]) =>
  phones.map((phone) => phone?.trim()).filter((phone): phone is string => !!phone);

/** Network (operator) and site contact details, keeping only usable values */
export const getPoiContacts = (poi: OcmPoi): { network: ContactDetails; site: ContactDetails } => {
  const operator = poi.OperatorInfo;
  const address = poi.AddressInfo;
  return {
    network: {
      phones: toPhones(operator?.PhonePrimaryContact, operator?.PhoneSecondaryContact),
      email: toValidEmail(operator?.ContactEmail),
      websiteUrl: getSafeUrl(operator?.WebsiteURL),
      bookingUrl: getSafeUrl(operator?.BookingURL),
    },
    site: {
      phones: toPhones(address?.ContactTelephone1, address?.ContactTelephone2),
      email: toValidEmail(address?.ContactEmail),
      websiteUrl: getSafeUrl(address?.RelatedURL),
      bookingUrl: null,
    },
  };
};

export const hasContactDetails = ({ phones, email, websiteUrl, bookingUrl }: ContactDetails) =>
  phones.length > 0 || !!email || !!websiteUrl || !!bookingUrl;

/** i18n keys for how drivers pay or get access at the site */
export const getPaymentRequirementKeys = (usage?: OcmUsageType | null) =>
  [
    usage?.IsMembershipRequired && 'nearby_chargers_membership_required',
    usage?.IsPayAtLocation && 'nearby_chargers_pay_at_location',
    usage?.IsAccessKeyRequired && 'nearby_chargers_access_key_required',
  ].filter((key): key is string => !!key);

// ChargeHub's free embeddable map (from its public embed generator) is the only ChargeHub page
// that can be centred by coordinates; it can't mark a station, so it opens zoomed in far enough
// that the site is the pin at the centre. English only. Its connector/level filters hide every
// pin, so they are not used.
const CHARGEHUB_EMBED_MAP_URL = 'https://chargehub.com/en/embedwithfilters.html';
const CHARGEHUB_CLOSE_ZOOM = 19;

/** ChargeHub map centred on the site, showing its live availability pins */
export const getChargeHubMapUrl = ({ latitude, longitude }: FinderResult) =>
  `${CHARGEHUB_EMBED_MAP_URL}?lat=${latitude}&lon=${longitude}&zoom=${CHARGEHUB_CLOSE_ZOOM}`;
